/**
 * State 2 of 9 — empty, with guidance (spec 01 §18, `PRODUCT.md` §4).
 *
 * **Driven by:** a `200` whose list envelope carries `data: []` — `OffsetListEnvelope`
 * or `CursorListEnvelope` in `openapi.yaml`, with `total: 0`. Never by a failed
 * request: an error that renders as "no data" is how an outage gets mistaken for a
 * quiet week.
 *
 * `title` and `description` are required to be supplied by the caller rather than
 * defaulted here, because `PRODUCT.md` §4 makes the point that matters: "no data"
 * and "no devices enrolled yet" are different facts, and a shared component that
 * writes one sentence for nine screens erases the difference. The component owns the
 * layout and the affordance; the screen owns the sentence.
 */

import type { ReactNode } from 'react';

import { Icon } from './icons';

export interface EmptyStateProps {
  readonly title: string;
  /** What "empty" means here, and what would fill it. */
  readonly description?: string;
  /** The next action — a link or a button. Optional: some emptiness has no action. */
  readonly action?: ReactNode;
  readonly testId?: string;
}

export function EmptyState({
  title,
  description,
  action,
  testId = 'state-empty',
}: EmptyStateProps): ReactNode {
  return (
    <div className="wp-empty" role="status" data-testid={testId}>
      <Icon name="inbox" className="wp-note__icon" />
      <p className="wp-note__label">{title}</p>
      {description === undefined ? null : <p className="wp-muted">{description}</p>}
      {action === undefined ? null : <div className="wp-note__actions">{action}</div>}
    </div>
  );
}
